<template>
  <div :class="$style.container">
    <div v-if="!showTextsOnly" :class="[$pgCTStyle.headerContainer, $style.headerContainer]">
      <div v-if="textTitle" :class="$style.titleText">
        {{ textTitle }}
      </div>
      <div v-else :class="$style.titleText">
        {{ $t(disciplineOptions.find((option) => option.selectionValue === discipline)?.label || "unknown") }}
        {{ codeLanguage ? " - " + codeLanguage : "" }}
      </div>

      <div :class="$style.actionsContainer">
        <div :title="$t('text-details')" :class="$pgCTStyle.actionIconContainer" @click="openModal">
          <img :class="$pgCTStyle.actionIcon" :src="infoIcon" alt="info" />
        </div>

        <div
          :title="$t('view-texts')"
          v-if="!editTexts && typeof openEnlargedGameTexts === 'function'"
          :class="$pgCTStyle.actionIconContainer"
          @click="openEnlargedGameTexts"
        >
          <img :class="$pgCTStyle.actionIcon" :src="expandIcon" alt="expand" />
        </div>
      </div>
    </div>

    <!-- BODY -->
    <div :class="$style.bodyContainer">
      <!-- SELECTION TABS -->
      <div :class="$style.selectionContainer">
        <div
          v-for="(text, index) in editableTexts"
          :key="index"
          :class="[$style.selectionItem, { active: viewMode === 'text' && selectedTextIndex === index }]"
        >
          <div :class="$style.selectionItemRow">
            <div @click="selectText(index)">
              <PGTextSelectionButton
                :text="`${$t('text')} ${text.label}`"
                :clickFunction="() => {}"
                backBg="#000000"
                :frontBg="viewMode === 'text' && selectedTextIndex === index ? '#676767' : '#E9E9E9'"
                :textColor="viewMode === 'text' && selectedTextIndex === index ? '#FFFFFF' : '#000000'"
                :buttonWidth="isMobile ? '60px' : isTablet ? '70px' : '90px'"
              />
            </div>
          </div>

          <div v-if="realTextId && text.id == realTextId" :class="$style.itemHighlighter"></div>
        </div>

        <div v-if="showImageButton && !!imageUrl" :class="[$style.selectionItem, { active: viewMode === 'image' }]">
          <div :class="$style.selectionItemRow" @click.stop="selectImage">
            <PGTextSelectionButton
              :text="$t('image')"
              :clickFunction="() => {}"
              backBg="#000000"
              :frontBg="viewMode === 'image' ? '#676767' : (imageUrl ? '#E9E9E9' : '#CFCFCF')"
              :textColor="viewMode === 'image' ? '#FFFFFF' : '#000000'"
              :buttonWidth="isMobile ? '60px' : isTablet ? '70px' : '90px'"
            />
          </div>
        </div>
      </div>

      <!-- BLACK PANEL CONTENT -->
      <div :class="$style.textsContainer">
        <textarea
          v-if="
            viewMode === 'text' &&
            selectedTextIndex !== null &&
            editableTexts[selectedTextIndex]
          "
          v-model="editableTexts[selectedTextIndex].content"
          :class="[$style.editableTextArea, $style.customScroll]"
          @input="updateTexts"
          :readonly="!editTexts"
        ></textarea>

        <div
          v-else-if="viewMode === 'text'"
          :class="[$style.editableTextArea, $style.customScroll, $style.noImage]"
        >
          {{ $t("no-game-texts") }}
        </div>

        <div v-else-if="viewMode === 'image'" :class="$style.imagePanel">
          <img v-if="imageUrl" :src="imageUrl" :class="$style.panelImage" alt="game image" />
          <div v-else :class="$style.noImage">No image available</div>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div v-if="isModalOpen" :class="$utilModalStyle.modalOverlay" @click="closeModal">
      <div :class="[$utilModalStyle.modalContent]" @click.stop>
        <h3 :class="$utilModalStyle.modalTitle">{{ $t("game-details") }}</h3>
        <div :class="$utilModalStyle.modalBody">
          <p :class="$style.modelTextItem" v-if="discipline">
            <strong>{{ $t("discipline") }}:</strong>
            {{ $t(disciplineOptions.find((option) => option.selectionValue === discipline)?.label || "unknown") }}
          </p>
          <p :class="$style.modelTextItem" v-if="codeLanguage">
            <strong>{{ $t("code-language") }}:</strong> {{ codeLanguage }}
          </p>
          <p :class="$style.modelTextItem" v-if="textTitle">
            <strong>{{ $t("title") }}:</strong> {{ textTitle }}
          </p>
        </div>
        <div :class="$utilModalStyle.modalActions">
          <button :class="$utilModalStyle.primaryAction" @click="closeModal">{{ $t("close") }}</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import styles from "@/css/GameTextsBox.module.css";
import playGameComponentsTemplateStyles from "@/css/PlayGameComponentsTemplate.module.css";
import globalStyles from "@/css/global.module.css";
import utilModalStyles from "@/css/UtilModal.module.css";
import InfoIcon from "@/assets/icons/info.svg";
import ExpandIcon from "@/assets/icons/expand.svg";
import { disciplineOptions } from "@/utils/data";
import PGTextSelectionButton from "../buttons/PGTextSelectionButton.vue";
import useDeviceType from "@/utils/useDeviceType";

const device = useDeviceType();

export default {
  components: { PGTextSelectionButton },
  name: "GameTextsBox",
  props: {
    texts: {
      type: Array,
      required: true,
      default: () => [],
    },
    editTexts: {
      type: Boolean,
      required: false,
      default: false,
    },
    discipline: {
      type: String,
      required: false,
    },
    codeLanguage: {
      type: String,
      required: false,
    },
    textTitle: {
      type: String,
      required: false,
    },
    realTextId: {
      type: Number,
      required: false,
    },
    realTextLabel: {
      type: String,
      required: false,
    },
    // isHost: {
    //   type: Boolean,
    //   required: true,
    // },
    showTextsOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    openEnlargedGameTexts: {
      type: Function,
      required: false,
      default: null,
    },
    imageUrl: {
      type: String,
      required: false,
      default: "",
    },
    showImageButton: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      infoIcon: InfoIcon,
      expandIcon: ExpandIcon,
      isModalOpen: false, // Modal state
      editableTexts: this.texts ? JSON.parse(JSON.stringify(this.texts)) : [], // Prevent parsing undefined
      selectedTextIndex: 0, // Default to the first text
      disciplineOptions: disciplineOptions,
      viewMode: "text",
      // textIndexes: {
      //   1: "A",
      //   2: "B",
      //   3: "C",
      //   4: "D",
      // },
    };
  },
  computed: {
    $style() {
      return styles;
    },
    $globalStyle() {
      return globalStyles;
    },
    $utilModalStyle() {
      return utilModalStyles;
    },
    $pgCTStyle() {
      return playGameComponentsTemplateStyles;
    },
    width() {
      return device.width.value;
    },
    height() {
      return device.height.value;
    },
    isMobile() {
      return device.isMobile.value;
    },
    isTablet() {
      return device.isTablet.value;
    },
    isDesktop() {
      return device.isDesktop.value;
    },
  },
  methods: {
    syncEditableTexts(texts) {
      this.editableTexts = Array.isArray(texts)
        ? JSON.parse(JSON.stringify(texts))
        : [];

      if (!this.editableTexts.length) {
        this.selectedTextIndex = null;
        this.viewMode = this.imageUrl ? "image" : "text";
        return;
      }

      if (
        this.selectedTextIndex === null ||
        this.selectedTextIndex >= this.editableTexts.length
      ) {
        this.selectedTextIndex = 0;
      }

      if (this.viewMode !== "image") {
        this.viewMode = "text";
      }
    },
    selectText(index) {
      this.viewMode = "text";
      this.selectedTextIndex = index; // Update selected text
    },
    updateTexts() {
      // Emit updated texts to the parent
      this.$emit("update-texts", this.editableTexts);
    },
    openModal() {
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    selectImage() {
      if (!this.imageUrl) return;
      this.viewMode = "image";
      this.selectedTextIndex = null;
    }
  },
  watch: {
    texts: {
      immediate: true,
      deep: true,
      handler(newTexts) {
        this.syncEditableTexts(newTexts);
      },
    },
  },
};
</script>

<style scoped></style>